import React, { useState, ReactNode, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../config/theme.config";
import UnverifiedAccountModal from "../common/UnverifiedAccountModal";
import {
  faUserCircle,
  faSignOutAlt,
  faTruckFast,
  faBell,
  faTachometerAlt,
  faChevronLeft,
  faBars,
  faAddressBook,
  faUser,
  faTimes,
  faBoxOpen,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons";
import "../../styles/app/DashboardLayout.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Footer from "../app/Footer";
import Nav from "../app/Nav";
import TopBar from "../common/TopBar";
import elPaketLogoIMG from "../../assets/images/logo-elpaket.png";

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
const { user, customerData, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem("customerSidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isVerify = localStorage.getItem("isVerify") === "true";
  const hasSeenUnverifiedModal = localStorage.getItem("hasSeenUnverifiedModal") === "true";
  const [showUnverifiedModal, setShowUnverifiedModal] = useState(!hasSeenUnverifiedModal && !isVerify && !location.pathname.includes("send-package"));

  useEffect(() => {
    if (location.pathname.includes("send-package")) {
      setShowUnverifiedModal(false);
      localStorage.setItem("hasSeenUnverifiedModal", "true");
    }
  }, [location.pathname]);

  const handleCloseUnverifiedModal = () => {
    setShowUnverifiedModal(false);
    localStorage.setItem("hasSeenUnverifiedModal", "true");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const brandName = process.env.REACT_APP_BRAND_NAME;

  const getGreetingName = () => {
    if (customerData?.FirstName) return customerData.FirstName;
    if (user?.displayName) return user.displayName.split(" ")[0];
    return "Usuario";
  };

  const navItems = [
    {
      path: "/customer/dashboard",
      icon: faTachometerAlt,
      name: "Dashboard",
    },
    {
      path: "/customer/address",
      icon: faAddressBook,
      name: "Mis Direcciones",
    },
    {
      path: "/customer/my-orders",
      icon: faBoxOpen,
      name: "Mis Envíos",
    },
    {
      path: "/customer/my-payments",
      icon: faCreditCard,
      name: "Mis Pagos",
    },
    {
      path: "/customer/profile",
      icon: faUserCircle,
      name: "Mi Perfil",
    },
    {
      path: "",
      icon: faSignOutAlt,
      name: "Salir",
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <TopBar />
      <div className="dashboard-nav-wrapper">
        <Nav />
      </div>

      {/* Mobile Nav Header (mimics main Nav) */}
      <header className="dashboard-mobile-nav">
        <div className="mobile-nav-content">
          <div
            className="mobile-nav-left"
            onClick={() => navigate("/")}
            style={{ cursor: "pointer" }}
          >
            <img 
              src={elPaketLogoIMG} 
              alt="elPaket" 
              className="nav-logo-img" 
              style={{ height: "44px", objectFit: "contain" }}
            />
          </div>
          <button
            className="mobile-hamburger"
            onClick={() => {
              setIsMobileOpen(!isMobileOpen);
              if (!isMobileOpen) {
                setIsCollapsed(false);
                localStorage.setItem("customerSidebarCollapsed", "false");
              }
            }}
            aria-label="Toggle sidebar"
          >
            <FontAwesomeIcon icon={isMobileOpen ? faTimes : faBars} />
          </button>
        </div>
      </header>
      <div className="dashboard-layout">
        <aside
          className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isMobileOpen ? "mobile-open" : ""}`}
        >
          <div
            className="sidebar-toggle-btn"
            onClick={() => {
              const nextVal = !isCollapsed;
              setIsCollapsed(nextVal);
              localStorage.setItem("customerSidebarCollapsed", JSON.stringify(nextVal));
            }}
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              className={isCollapsed ? "icon-collapsed" : ""}
            />
          </div>
          <div className="dashboard-layout-sidebar-user-box">
            <div className="user-box-header"></div>
            <div className="user-box-icon">
              <FontAwesomeIcon icon={faUserCircle} />
            </div>
            <div className="user-box-body">
              <h3>{customerData?.FirstName + " " + customerData?.LastName}</h3>
              <p>{customerData?.Email}</p>
              {customerData?.Code && (
                <p style={{ fontSize: "11px", opacity: 0.7, marginTop: "2px" }}>
                  {customerData.Code}
                </p>
              )}
            </div>
          </div>
          <ul className="sidebar-nav">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? "active" : ""}`}
                  onClick={(e) => {
                    setIsMobileOpen(false); // Cierra el menú móvil

                    // Si ya estamos en la ruta, forzamos que se abra de nuevo (reset de estado)
                    if (location.pathname === item.path && item.path !== "") {
                      window.location.href = item.path;
                    }

                    if (item.name === "Salir") {
                      e.preventDefault();
                      handleLogout();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={item.icon} className="link-icon" />
                  <span>{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        <div
          className={`sidebar-overlay ${isMobileOpen ? "visible" : ""}`}
          onClick={() => setIsMobileOpen(false)}
        ></div>

        <div className="dashboard-content">
          <main className="dashboard-main">{children}</main>
        </div>
      </div>
      <Footer />
      <UnverifiedAccountModal
        show={showUnverifiedModal}
        onClose={handleCloseUnverifiedModal}
        email={customerData?.Email}
      />
    </>
  );
};

export default DashboardLayout;
